/**
 * Keeping calls in progress on the microphone and speaker to use
 *
 * A call has to move when the device it should use changes: the user picked another one, or a device came or went and
 * the preferred lists now resolve elsewhere. SipClient used to learn this from the MediaDeviceSelected and
 * MediaDeviceChange events and acted on the microphone ones only. The topbar picks a headset by setting the microphone
 * and then the speaker, so the call switched on the microphone event - while the speaker still resolved to the
 * previous pick - and the speaker event was dropped: the call's speaker trailed one selection behind its microphone.
 *
 * Watching what the ids resolve to instead lands a microphone and speaker picked together in one switch, catches a
 * speaker that changes on its own, and leaves the call alone when a device that is not in use comes or goes.
 *
 * The camera is watched the same way, on its own: a call's camera moves with replaceTrack and no renegotiation, a
 * different path from the microphone and speaker.
 */

import { watch } from 'vue'
import { MediaDeviceType, type MediaDevice } from '@/stores/MediaDevices.types'

export interface CallAudioDeviceChange {
	/** The microphone to use changed */
	microphone: boolean
	/** The speaker to use changed */
	speaker: boolean
}

/** The part of the media devices store the watch reads */
export interface CallAudioDeviceSource {
	readonly microphoneDeviceId: string
	readonly speakerDeviceId: string
	readonly devices: readonly MediaDevice[]
}

/**
 * Identify the device a call is really on. That is the id, except for the system default: 'default' stands for whichever
 * device the system points it at right now, which changes - or is unplugged - while the id stays the same.
 * @param id Resolved device id
 * @param kind Kind of device
 * @param devices Devices as enumerated right now
 * @returns A value that changes exactly when the call has to move
 */
function deviceKey(id: string, kind: MediaDeviceType, devices: readonly MediaDevice[]) {
	if (id !== 'default') { return id }
	return `default:${devices.find(device => device.deviceId === 'default' && device.kind === kind)?.rawLabel ?? ''}`
}

/**
 * Call back when the microphone or speaker to use changes - once for changes made together
 * @param source The media devices store
 * @param onChange Receives which of the two changed
 * @returns Stops watching
 */
export function watchCallAudioDevices(source: CallAudioDeviceSource, onChange: (change: CallAudioDeviceChange) => void) {
	return watch([
		() => deviceKey(source.microphoneDeviceId, MediaDeviceType.audioinput, source.devices),
		() => deviceKey(source.speakerDeviceId, MediaDeviceType.audiooutput, source.devices)
	], ([microphone, speaker], [oldMicrophone, oldSpeaker]) => {
		onChange({ microphone: microphone !== oldMicrophone, speaker: speaker !== oldSpeaker })
	})
}

/** The part of the media devices store the camera watch reads */
export interface CallCameraSource {
	readonly videoDeviceId: string | null
}

/**
 * Call back when the camera to use changes - another camera chosen, "Benyt ikke", or the chosen camera coming or going
 * @param source The media devices store
 * @param onChange Receives the camera to use now and the one used before - null for none
 * @returns Stops watching
 */
export function watchCallCamera(source: CallCameraSource, onChange: (camera: string | null, previous: string | null) => void) {
	return watch(() => source.videoDeviceId, (camera, previous) => { onChange(camera, previous) })
}
